import { NewCreateFlowPayload } from '../../types/ApiTypes';
import { FlowsResponse } from '../../types/FlowsTypes';
import useFlowsConversation from './useFlowsConversation';
import useFlowsInitCycle from './useFlowsInitCycle';
import useFlowsCreation from './useFlowsCreation';
import useFlowsStructureModifications from './useFlowsStructureModifications';
import useFlowsInitRoutes from './useFlowsInitRoutes';
import useSessionId from '../useSessionId';

interface FlowsInitParams {
  /** The flowId that a request should be made for. */
  flowId: string;
  /** Query params are used by BE to filter the questions that should be contained in.  */
  payload: NewCreateFlowPayload;
  /** The URL where the user came from */
  inputUrl?: string;
  /** The URL where the user should be redirected to once a flow is finished. */
  outputUrl: string;
  /** A session id that the flow is based on */
  sessionId?: string;
  /** If true the flow will be resumed from the correct position */
  resumeFlow?: boolean;
  /** If the flow should do the complete call at the end */
  shouldComplete?: boolean;
}

interface FlowsInit {
  initFlow: (params: FlowsInitParams) => Promise<string | void>;
  initViewOnlyFlow: (params: FlowsInitParams) => Promise<void>;
}

export interface FlowFunctionParams {
  flow: FlowsResponse;
  initParams: FlowsInitParams;
}

const useFlowsInitNew = (): FlowsInit => {
  const qSessionId = useSessionId();
  const { startFlowInit, finishFlowInit } = useFlowsInitCycle();
  const { checkConversationCompletion, getPartyFromConversation } =
    useFlowsConversation();
  const { createFlow, resumeFlow } = useFlowsCreation();
  const { prepareFlowForInit, disableAllQuestions } =
    useFlowsStructureModifications();
  const { pushUserToFlow, pushUserToViewOnly } = useFlowsInitRoutes();

  const initFlow = async (initParams: FlowsInitParams) => {
    const sessionId = initParams.sessionId || qSessionId;
    startFlowInit(initParams.flowId);
    if (sessionId) {
      await checkConversationCompletion(sessionId);

      // Check if the party_id is already set in the payload
      if (!initParams.payload.party_id) {
        // If not, get the party_id from the conversation
        const partyId = await getPartyFromConversation(sessionId);
        if (partyId) {
          initParams.payload.party_id = partyId;
        }
      }
    }

    const flow = await createFlow({
      flowId: initParams.flowId,
      payload: initParams.payload,
    });
    if (flow) {
      prepareFlowForInit({
        flow,
        initParams,
      });
      // 1 of the 2 will always be defined
      await finishFlowInit(flow.session_id);

      if (initParams.resumeFlow) {
        await resumeFlow({
          flow,
          initParams,
        });
      } else {
        pushUserToFlow({
          flow,
          initParams,
        });
      }

      return flow.session_id;
    }

    console.error('Something went wrong while initializing the flow');
  };

  /**
   * This will init the flow in view only made which make it impossible to answer the flow.
   * This should be mainly used to build other flows based on 1 flow.
   */
  const initViewOnlyFlow = async (initParams: FlowsInitParams) => {
    startFlowInit(initParams.flowId, true);
    let flow = await createFlow({
      flowId: initParams.flowId,
      payload: initParams.payload,
      persist: false,
    });

    if (flow) {
      flow = disableAllQuestions(flow);
      prepareFlowForInit({
        flow,
        initParams,
      });
      // 1 of the 2 will always be defined
      await finishFlowInit(initParams.sessionId || qSessionId);
      pushUserToViewOnly({
        flow,
        initParams,
      });
    } else {
      console.error('Something went wrong while initializing the flow');
    }
  };

  return { initFlow, initViewOnlyFlow };
};

export default useFlowsInitNew;
